import { ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment-timezone';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PICKUP_DATE_TYPE_OPTIONS, PICKUP_WINDOW_TYPE_OPTIONS } from 'src/app/MyQuote/AddQuote/Steps/addQuoteSteps.const';
import { getSpecialInstructionValues, resolveSpecialInstructionText } from 'src/app/Orders/order.helpers';
import { getTimeUntilStart } from 'src/app/Orders/schedule.helpers';
import HeaderCmp from 'src/components/HeaderCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { ORDER_STATUS } from 'src/constants/common';
import { appearance } from 'src/redux/basic.slice';
import { useAppSelector } from 'src/redux/hooks';
import { getDateWithSuffixFormat } from 'src/utils/CommonFunctions';

import { useRadarData } from '../../hooks/useRadarData';
import { formatTimeRangeDisplayNew, getTimeZoneOption, makeFormatScheduleDisplay } from '../track.helpers';

import CardInfoRow from './common/CardInfoRow';
import ContactCopyButton from './common/ContactCopyButton';
import InfoRow from './common/InfoRow';
import { SHARE_MOBILE_CARD, SHARE_MOBILE_CARD_TITLE } from './common/shareMobile.styles';

// isShareMobile: share-link mobile sheet (Schedule tab) — Figma card layout, same data + interactions.
const PickupStateSection = ({ isShareLink = false, titleContent, isShareMobile = false }: { isShareLink?: boolean; titleContent?: React.ReactNode; isShareMobile?: boolean }) => {
    const { t } = useTranslation();
    const { trackOrderDetail, isDetailLoading, selectedLeg } = useRadarData();
    const appearanceData = useAppSelector(appearance);
    const userTimezone = appearanceData?.timezone || 'UTC';
    const [isDetailOpen, setIsDetailOpen] = useState(true);
    const timeRef = useRef<HTMLParagraphElement>(null);

    const activeLeg = useMemo(() => selectedLeg ?? trackOrderDetail?.legs?.[0] ?? null, [selectedLeg, trackOrderDetail]);

    // Special Instructions come from the LEG (order_legs.specialInstruction) — the same
    // field WAL's radar reads. orders.specialNotes is the legacy quote-era column and is
    // empty on orders created through the current flow, which is why this row rendered blank.
    const resolvedSpecialInstruction = useMemo(
        () =>
            resolveSpecialInstructionText(
                activeLeg?.specialInstruction,
                getSpecialInstructionValues({
                    // Quote ID is an internal reference the public share page hides (see
                    // hideInternalReferenceRows in OrderDetailsSection), so @[quoteNumber]
                    // must resolve to blank there instead of leaking it into the text.
                    quoteNumber: isShareLink ? null : activeLeg?.quoteCode,
                    additionalServices: trackOrderDetail?.additionalServices,
                })
            ),
        [activeLeg?.specialInstruction, activeLeg?.quoteCode, trackOrderDetail?.additionalServices, isShareLink]
    );

    const schedule = activeLeg?.schedule ?? null;
    // No shipper zone → no source to convert FROM. A hardcoded 'UTC' here would convert
    // the Local Time row from the wrong zone rather than leaving it unconverted.
    const shipperTimezone = activeLeg?.shipper?.timezone || null;

    const pickupSchedule = useMemo(() => makeFormatScheduleDisplay('pickup', schedule, shipperTimezone), [schedule, shipperTimezone]);
    const pickupDisplay = pickupSchedule.display;

    const actualPickupDatetime = schedule?.actualPickupDatetime ?? null;
    const isPickedUpOrder = Boolean(trackOrderDetail?.orderStatus && trackOrderDetail.orderStatus !== ORDER_STATUS.DISPATCHED && actualPickupDatetime);
    const actualPickupMoment = actualPickupDatetime ? moment.utc(actualPickupDatetime).tz(shipperTimezone || userTimezone) : null;

    // Moment escapes literal text wrapped in [..]; injecting the translated word into the
    // brackets keeps it a single format string while letting i18n control the separator.
    const atToken = `[${t('Common.at')}]`;
    const pickedUpLabel = actualPickupMoment
        ? `${t('Radar.Picked_Up')} ${getDateWithSuffixFormat(
              actualPickupMoment.calendar(null, {
                  sameDay: `[Today], ${atToken} HH:mm`,
                  lastDay: `[Yesterday], ${atToken} HH:mm`,
                  lastWeek: `MMM Do, YYYY ${atToken} HH:mm`,
                  nextDay: `MMM Do, YYYY ${atToken} HH:mm`,
                  nextWeek: `MMM Do, YYYY ${atToken} HH:mm`,
                  sameElse: `MMM Do, YYYY ${atToken} HH:mm`,
              })
          )}`
        : '';

    const sensitivityLabel = PICKUP_DATE_TYPE_OPTIONS.find((opt) => opt.value === schedule?.currentPickupSensitivity)?.label;
    const windowTypeLabel = PICKUP_WINDOW_TYPE_OPTIONS.find((opt) => opt.value === schedule?.currentPickupWindowType)?.label;
    // Local Time is converted into the VIEWER's timezone, so the abbreviation beside it must
    // be the viewer's too. display.timezoneLabel is the SOURCE (shipper) zone — using it here
    // would label a converted time with the origin's abbreviation.
    const timezoneAbbr = getTimeZoneOption(userTimezone)?.abbr ?? '';

    const displayMonth = isPickedUpOrder ? actualPickupMoment?.format('MMM').toUpperCase() ?? '-' : pickupDisplay?.month ?? '-';
    const displayDay = isPickedUpOrder ? actualPickupMoment?.format('DD') ?? '-' : pickupDisplay?.day ?? '-';

    const hasTimeRange = pickupDisplay?.timeRange && pickupDisplay.timeRange !== '-';
    const windowValue = windowTypeLabel || hasTimeRange ? `${windowTypeLabel ? `${windowTypeLabel} ` : ''}${hasTimeRange ? pickupDisplay?.timeRange : ''}`.trim() : '-';
    // WAL renders this row as soon as a schedule exists: the converted range when there IS one,
    // otherwise the viewer's zone abbreviation on its own ("EDT"). Requiring a real range meant
    // every Business-Hours schedule (no start/end time, so timeRangeLocal falls back to "-")
    // showed a bare dash here while WAL showed the zone.
    const localTimeRange = pickupDisplay?.timeRangeLocal && pickupDisplay.timeRangeLocal !== '-' ? pickupDisplay.timeRangeLocal : '';
    const localTimeLabel = timezoneAbbr || userTimezone || '';
    const localTimeValue = pickupDisplay?.timeRangeLocal ? (
        <span className="text-xs leading-[18px] font-medium text-textSecondary inline-flex items-center gap-x-1">
            {localTimeRange && <span>{localTimeRange}</span>}
            {/* Abbreviations are ambiguous across zones (several read "EDT"), so the full IANA
                name sits behind a tooltip — same affordance as WAL. */}
            <TooltipCmp message={userTimezone || ''}>
                <span className="cursor-default">{localTimeLabel}</span>
            </TooltipCmp>
        </span>
    ) : undefined;
    // "Pickup Window (09:00 to 16:00 EDT)" — the headline phrase, plus a countdown when the
    // window opens within 24h. Both mirror wal-frontend-v2's Radar pickup card.
    const windowPhrase = formatTimeRangeDisplayNew(pickupDisplay, t);
    const timeUntilStart = pickupSchedule.startDate ? getTimeUntilStart(pickupSchedule.startDate, t) : '';

    if (isShareMobile) {
        return (
            <>
                {/* Pickup Schedule — switcher + collapse toggle, date row, detail rows */}
                <div className={`${SHARE_MOBILE_CARD} p-2 flex flex-col gap-2`}>
                    <div className="flex items-center gap-2 pb-1">
                        <div className="flex-1 min-w-0 flex items-center">{titleContent ?? <p className={SHARE_MOBILE_CARD_TITLE}>{t('Radar.Pickup_Schedule')}</p>}</div>
                    </div>

                    <div className="flex items-start gap-1.5">
                        <div
                            className={`h-[38px] min-w-[38px] px-[7px] py-1.5 rounded border border-utilityGray200 bg-white flex flex-col items-center justify-center text-center flex-none ${
                                isDetailLoading ? 'custom-loading' : ''
                            }`}
                        >
                            {displayMonth && displayMonth !== '-' ? (
                                <>
                                    <span className="text-[10px] font-semibold text-primary700 leading-normal uppercase">{isDetailLoading ? 'lorem' : displayMonth}</span>
                                    <span className="text-[10px] font-semibold text-primary700 leading-[14px]">{isDetailLoading ? 'ip' : displayDay}</span>
                                </>
                            ) : (
                                <p className="text-[10px] font-semibold text-primary700">{t('Common.N/A')}</p>
                            )}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <p
                                className={`text-xs font-medium leading-[18px] text-grayLight900 ${isDetailLoading ? 'custom-loading' : ''}`}
                                // Generated by internal utilities (getDateWithSuffixFormat) — safe by construction.
                                dangerouslySetInnerHTML={{
                                    __html: isDetailLoading ? 'lorem ipsum dolor' : isPickedUpOrder ? pickedUpLabel : pickupDisplay?.formattedDateRange ?? '-',
                                }}
                            />
                            <div className={`text-xs leading-[18px] font-medium text-grayLight600 flex items-center gap-x-1.5 min-w-0 ${isDetailLoading ? 'custom-loading' : ''}`}>
                                {isDetailLoading || !isPickedUpOrder ? (
                                    <>
                                        <TruncateTextCmp message={isDetailLoading ? '' : windowPhrase} refName={timeRef}>
                                            <p className="truncate" ref={timeRef}>
                                                {isDetailLoading ? 'lorem ipsum dolor sit amet' : windowPhrase || windowTypeLabel || '-'}
                                            </p>
                                        </TruncateTextCmp>
                                        {windowPhrase && timeUntilStart ? (
                                            <p className="flex-none flex items-center gap-x-1">
                                                <span className="bg-successSecondary w-1.5 h-1.5 rounded-full"></span>
                                                {`${t('Common.in')} ${timeUntilStart}`}
                                            </p>
                                        ) : (
                                            ''
                                        )}
                                    </>
                                ) : (
                                    t('Radar.Pickup_Completed')
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Detail rows — always shown in full on the share sheet */}
                    <div className="border-t border-utilityGray200 pt-2 flex flex-col gap-2">
                        <CardInfoRow isShareMobile label={t('Radar.Pickup_Sensitivity')} value={sensitivityLabel} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} />
                        <CardInfoRow isShareMobile label={t('Radar.Pickup_Window', { type: 'Pickup' })} value={windowValue} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} />
                        <CardInfoRow isShareMobile label={t('Radar.Local_Time')} value={localTimeValue} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} />
                    </div>
                </div>

                {/* Scheduling Details */}
                <div className={`${SHARE_MOBILE_CARD} p-2 flex flex-col gap-2`}>
                    <p className={`${SHARE_MOBILE_CARD_TITLE} pb-1`}>{t('ShareRadarOrder.Scheduling_Details')}</p>
                    <InfoRow isShareMobile label={t('Orders.Shipper_Name')} value={activeLeg?.shipper?.companyName} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} />
                    <InfoRow
                        isShareMobile
                        label={t('Orders.Shipper_Contact')}
                        value={activeLeg?.shipper?.contactName}
                        icon={activeLeg?.shipper?.contactName ? <ContactCopyButton phoneNumber={activeLeg?.shipper?.phoneNumber} /> : undefined}
                        isLoading={isDetailLoading}
                        isLabelLoading={isDetailLoading}
                    />
                    <InfoRow isShareMobile label={t('Orders.Pickup_Address')} value={activeLeg?.shipper?.fullAddress} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} />
                    <InfoRow
                        isShareMobile
                        label={t('Radar.Pickup_Instructions')}
                        value={activeLeg?.pickupInstruction ?? undefined}
                        isLoading={isDetailLoading}
                        isLabelLoading={isDetailLoading}
                        align="start"
                    />
                    <InfoRow
                        isShareMobile
                        label={t('Radar.Special_Instructions')}
                        value={resolvedSpecialInstruction || undefined}
                        isLoading={isDetailLoading}
                        isLabelLoading={isDetailLoading}
                        align="start"
                    />
                </div>
            </>
        );
    }

    return (
        <>
            <div className="w-full rounded-lg shadow-md bg-white overflow-hidden">
                {/* Header */}
                <HeaderCmp title={titleContent ? '' : t('Radar.Pickup_Schedule')} leftSideContent={titleContent} isHeaderWithBg={false} parentClassName="!border-b-0 px-4" />

                {/* Body */}
                <div className="px-4 pb-4 flex flex-col gap-y-3">
                    {/* Date card */}
                    <div className="p-3 rounded-lg border flex flex-col border-utilityGray200 bg-gray50 overflow-hidden">
                        <div className="flex items-center gap-x-1.5 cursor-pointer" onClick={() => setIsDetailOpen((prev) => !prev)}>
                            <div
                                className={`table-profile-customer-circle-xl rounded-md bg-white flex flex-col items-center justify-center text-center flex-none gap-[1px] w-9 h-9 ${
                                    isDetailLoading ? 'custom-loading' : ''
                                }`}
                            >
                                {displayMonth && displayMonth !== '-' ? (
                                    <>
                                        <span className="text-[10px] font-semibold text-primary leading-[12px] uppercase tracking-wide">{isDetailLoading ? 'lorem' : displayMonth}</span>
                                        <span className="text-[10px] font-semibold text-primary leading-[14px]">{isDetailLoading ? 'ip' : displayDay}</span>
                                    </>
                                ) : (
                                    <p>{t('Common.N/A')}</p>
                                )}
                            </div>

                            <div className="flex-1 min-w-0">
                                <p
                                    className={`text-[12px] font-semibold leading-[18px] text-grayLight900 ${isDetailLoading ? 'custom-loading' : ''}`}
                                    // Content is generated by internal utilities (getDateWithSuffixFormat) — not user
                                    // input. DOMPurify is not a project dependency; this is safe by construction.
                                    dangerouslySetInnerHTML={{
                                        __html: isDetailLoading ? 'lorem ipsum dolor' : isPickedUpOrder ? pickedUpLabel : pickupDisplay?.formattedDateRange ?? '-',
                                    }}
                                />
                                <div className={`text-xs leading-[18px] font-medium text-grayLight600 flex items-center gap-x-1.5 ${isDetailLoading ? 'custom-loading' : ''}`}>
                                    {isDetailLoading || !isPickedUpOrder ? (
                                        <>
                                            <TruncateTextCmp message={isDetailLoading ? '' : windowPhrase} refName={timeRef}>
                                                <p className="truncate" ref={timeRef}>
                                                    {isDetailLoading ? 'lorem ipsum dolor sit amet' : windowPhrase || windowTypeLabel || '-'}
                                                </p>
                                            </TruncateTextCmp>
                                            {windowPhrase && timeUntilStart ? (
                                                <p className="flex-none flex items-center gap-x-1">
                                                    <span className="bg-successSecondary w-1.5 h-1.5 rounded-full"></span>
                                                    {`${t('Common.in')} ${timeUntilStart}`}
                                                </p>
                                            ) : (
                                                ''
                                            )}
                                        </>
                                    ) : (
                                        t('Radar.Pickup_Completed')
                                    )}
                                </div>
                            </div>

                            <TooltipCmp message={isDetailOpen ? t('Common.Collapse') : t('Common.Expand')}>
                                <ChevronDown className={`w-4 h-4 text-primary flex-none transition-transform duration-200 ${isDetailOpen ? 'rotate-180' : ''}`} />
                            </TooltipCmp>
                        </div>

                        <div
                            style={{
                                display: 'grid',
                                gridTemplateRows: isDetailOpen ? '1fr' : '0fr',
                                transition: 'grid-template-rows 300ms ease',
                            }}
                        >
                            <div className="overflow-hidden">
                                <div className="border bg-white mt-3 border-utilityGray200 px-3 py-3 rounded-md flex flex-col gap-y-2">
                                    <CardInfoRow label={t('Radar.Pickup_Sensitivity')} value={sensitivityLabel} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} />
                                    <CardInfoRow label={t('Radar.Pickup_Window', { type: 'Pickup' })} value={windowValue} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} />
                                    <CardInfoRow label={t('Radar.Local_Time')} value={localTimeValue} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Info rows */}
                    <div className="flex flex-col gap-y-1.5">
                        <InfoRow label={t('Orders.Shipper_Name')} value={activeLeg?.shipper?.companyName} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} />
                        <InfoRow
                            label={t('Orders.Shipper_Contact')}
                            value={activeLeg?.shipper?.contactName}
                            icon={activeLeg?.shipper?.contactName ? <ContactCopyButton phoneNumber={activeLeg?.shipper?.phoneNumber} /> : undefined}
                            isLoading={isDetailLoading}
                            isLabelLoading={isDetailLoading}
                        />
                        <InfoRow label={t('Orders.Pickup_Address')} value={activeLeg?.shipper?.fullAddress} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} align="start" />
                        <InfoRow label={t('Radar.Pickup_Instructions')} value={activeLeg?.pickupInstruction ?? undefined} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} align="start" />
                        <InfoRow label={t('Radar.Special_Instructions')} value={resolvedSpecialInstruction || undefined} isLoading={isDetailLoading} isLabelLoading={isDetailLoading} align="start" />
                    </div>
                </div>
            </div>
        </>
    );
};

export default PickupStateSection;
